import {
	createEffect,
	createSignal,
	createUniqueId,
	For,
	type JSX,
	Show,
	untrack,
} from "solid-js";
import type { RelevancyRating } from "../api/domain.ts";
import { useI18n } from "../i18n/provider.tsx";
import { formatDecimal, parseDecimal } from "../lib/format.ts";
import styles from "./Form.module.scss";

export type FieldProps = {
	label: string;
	hint?: string;
	optional?: boolean;
	/** Marks a field the domain insists on, such as a requirement's rationale. */
	required?: boolean;
	/** What is wrong with the current value; shown in place of the hint. */
	problem?: string;
	/** Receives the generated id so the control and the label stay connected. */
	children: (controlId: string) => JSX.Element;
};

export const Field = (props: FieldProps) => {
	const i18n = useI18n();
	const controlId = createUniqueId();

	return (
		<div class={styles.field}>
			<label class={styles.label} for={controlId}>
				{props.label}
				<Show when={props.optional === true}>
					<span class={styles.optional}>({i18n.t().common.optional})</span>
				</Show>
				<Show when={props.required === true}>
					<span class={styles.demanded} aria-hidden="true">
						*
					</span>
				</Show>
			</label>
			{props.children(controlId)}
			<Show
				when={props.problem !== undefined}
				fallback={
					<Show when={props.hint !== undefined}>
						<span class={styles.hint}>{props.hint}</span>
					</Show>
				}
			>
				<span class={styles.problem}>{props.problem}</span>
			</Show>
		</div>
	);
};

export type FieldGroupProps = {
	label: string;
	hint?: string;
	/** Marks a group the domain insists on, such as a capability's owning topic. */
	required?: boolean;
	/** What is wrong with the current selection; shown in place of the hint. */
	problem?: string;
	children: JSX.Element;
};

/**
 * A labelled field whose control is not one element, such as a checkbox tree.
 * `label for` needs a single control to point at, so here the label names a
 * group instead, which is what assistive technology can follow.
 */
export const FieldGroup = (props: FieldGroupProps) => {
	const i18n = useI18n();
	const labelId = createUniqueId();

	return (
		<div class={styles.field}>
			<span class={styles.label} id={labelId}>
				{props.label}
				<Show when={props.required === true}>
					<span class={styles.demanded} aria-hidden="true">
						*
					</span>
				</Show>
			</span>
			<div role="group" aria-labelledby={labelId}>
				{props.children}
			</div>
			<Show
				when={props.problem !== undefined}
				fallback={
					<Show when={props.hint !== undefined}>
						<span class={styles.hint}>{props.hint}</span>
					</Show>
				}
			>
				<span class={styles.problem}>{props.problem}</span>
			</Show>
		</div>
	);
};

export type TextFieldProps = {
	label: string;
	hint?: string;
	optional?: boolean;
	required?: boolean;
	problem?: string;
	placeholder?: string;
	value: string;
	multiline?: boolean;
	onInput: (value: string) => void;
	/** `password` for a secret the reader pastes, such as the access token. */
	type?: "text" | "password";
};

export const TextField = (props: TextFieldProps) => (
	<Field
		label={props.label}
		hint={props.hint}
		optional={props.optional}
		required={props.required}
		problem={props.problem}
	>
		{(controlId) => (
			<Show
				when={props.multiline === true}
				fallback={
					<input
						id={controlId}
						class={styles.input}
						type={props.type ?? "text"}
						placeholder={props.placeholder}
						value={props.value}
						onInput={(event) => props.onInput(event.currentTarget.value)}
					/>
				}
			>
				<textarea
					id={controlId}
					class={styles.textarea}
					placeholder={props.placeholder}
					value={props.value}
					onInput={(event) => props.onInput(event.currentTarget.value)}
				/>
			</Show>
		)}
	</Field>
);

export type Choice = {
	id: string;
	label: string;
};

export type ChoiceGroupProps = {
	name: string;
	legend?: string;
	choices: Choice[];
	/** Single choice: at most one id. Multiple choice: any number. */
	selected: string[];
	multiple?: boolean;
	onToggle: (id: string, checked: boolean) => void;
};

export const ChoiceGroup = (props: ChoiceGroupProps) => (
	<div class={styles.choices} role="group" aria-label={props.legend}>
		<For each={props.choices}>
			{(choice) => (
				<label class={styles.choice}>
					<input
						type={props.multiple === true ? "checkbox" : "radio"}
						name={props.name}
						value={choice.id}
						checked={props.selected.includes(choice.id)}
						onChange={(event) =>
							props.onToggle(choice.id, event.currentTarget.checked)
						}
					/>
					{choice.label}
				</label>
			)}
		</For>
	</div>
);

export type CheckListProps = {
	label: string;
	hint?: string;
	problem?: string;
	choices: Choice[];
	/** The ticked ids. Order is not kept; the choice order is what gets emitted. */
	picked: readonly string[];
	onChange: (picked: string[]) => void;
	/** Shown instead of the boxes when there is nothing to tick. */
	emptyLabel: string;
};

/**
 * Many-of-many over a flat list. Emits in choice order rather than click order,
 * so what gets stored does not depend on how the user got there.
 */
export const CheckList = (props: CheckListProps) => {
	const labelId = createUniqueId();
	const picked = () => new Set(props.picked);

	const toggle = (id: string, on: boolean) => {
		const next = new Set(picked());
		if (on) next.add(id);
		else next.delete(id);
		props.onChange(
			props.choices
				.filter((choice) => next.has(choice.id))
				.map((choice) => choice.id),
		);
	};

	return (
		<div class={styles.field}>
			<span class={styles.label} id={labelId}>
				{props.label}
			</span>
			<div class={styles.checkList} role="group" aria-labelledby={labelId}>
				<Show
					when={props.choices.length > 0}
					fallback={<span class={styles.hint}>{props.emptyLabel}</span>}
				>
					<For each={props.choices}>
						{(choice) => (
							<label class={styles.check}>
								<input
									type="checkbox"
									checked={picked().has(choice.id)}
									onChange={(event) =>
										toggle(choice.id, event.currentTarget.checked)
									}
								/>
								<span>{choice.label}</span>
							</label>
						)}
					</For>
				</Show>
			</div>
			<Show
				when={props.problem !== undefined}
				fallback={
					<Show when={props.hint !== undefined}>
						<span class={styles.hint}>{props.hint}</span>
					</Show>
				}
			>
				<span class={styles.problem}>{props.problem}</span>
			</Show>
		</div>
	);
};

export type RatingScaleProps = {
	name: string;
	legend?: string;
	value?: number;
	onPick: (value: number) => void;
};

const ratingSteps = [0, 1, 2, 3, 4, 5];

export const RatingScale = (props: RatingScaleProps) => (
	<div class={styles.scale} role="group" aria-label={props.legend}>
		<For each={ratingSteps}>
			{(step) => (
				<label class={`${styles.choice} ${styles.step}`}>
					<input
						type="radio"
						name={props.name}
						value={step}
						checked={props.value === step}
						onChange={() => props.onPick(step)}
						class={styles.hiddenControl}
					/>
					{step}
				</label>
			)}
		</For>
	</div>
);

/** The five steps of the relevancy scale, worst to best, with their glyphs. */
export const relevancySteps = [
	{ rating: "much_less", label: "--", tone: "muchLess" },
	{ rating: "less", label: "-", tone: "less" },
	{ rating: "equal", label: "=", tone: "equal" },
	{ rating: "more", label: "+", tone: "more" },
	{ rating: "much_more", label: "++", tone: "muchMore" },
] as const;

export type RelevancyScaleProps = {
	name: string;
	legend?: string;
	/** Absent means unrated, which is distinct from an explicit `equal`. */
	value?: RelevancyRating;
	onPick: (rating: RelevancyRating) => void;
};

/**
 * The five-way relevancy control.
 *
 * Unrated, every segment wears its own colour: the whole scale is the
 * invitation. Once a step is picked only that one keeps its colour and the rest
 * fall back to the track, so a filled row of these reads as a set of answers
 * rather than as decoration.
 */
export const RelevancyScale = (props: RelevancyScaleProps) => {
	const i18n = useI18n();
	return (
		<div
			class={`${styles.relevancy} ${props.value === undefined ? "" : styles.relevancyPicked}`}
			role="group"
			aria-label={props.legend}
		>
			<For each={relevancySteps}>
				{(step) => (
					<label
						class={`${styles.relevancyStep} ${styles[step.tone]}`}
						title={i18n.t().relevancy.scale[step.rating]}
					>
						<input
							type="radio"
							name={props.name}
							value={step.rating}
							checked={props.value === step.rating}
							onChange={() => props.onPick(step.rating)}
							class={styles.hiddenControl}
						/>
						<span aria-hidden="true">{step.label}</span>
					</label>
				)}
			</For>
		</div>
	);
};

export type SelectFieldProps = {
	label: string;
	hint?: string;
	optional?: boolean;
	required?: boolean;
	problem?: string;
	/** The choice shown while nothing is picked; absent means the first option. */
	placeholder?: string;
	choices: Choice[];
	value: string;
	onPick: (value: string) => void;
};

/** One-of-many where the list is long enough that radios would crowd the page. */
export const SelectField = (props: SelectFieldProps) => {
	let control: HTMLSelectElement | undefined;

	/*
	 * A value that arrives before its option does, as when the URL names a choice
	 * the list has yet to load, is dropped by the browser. Re-applying it once the
	 * options are there makes the control show what the caller holds.
	 */
	createEffect(() => {
		props.choices;
		const value = props.value;
		if (control !== undefined && control.value !== value) control.value = value;
	});

	return (
		<Field
			label={props.label}
			hint={props.hint}
			optional={props.optional}
			required={props.required}
			problem={props.problem}
		>
			{(controlId) => (
				<select
					ref={control}
					id={controlId}
					class={styles.input}
					value={props.value}
					onChange={(event) => props.onPick(event.currentTarget.value)}
				>
					<Show when={props.placeholder !== undefined}>
						<option value="">{props.placeholder}</option>
					</Show>
					<For each={props.choices}>
						{(choice) => <option value={choice.id}>{choice.label}</option>}
					</For>
				</select>
			)}
		</Field>
	);
};

export type NumberFieldProps = {
	label: string;
	hint?: string;
	optional?: boolean;
	value: number;
	/** Lowest value the field commits; anything under it snaps up on leaving. */
	min?: number;
	/** Highest value the field commits; anything over it snaps down on leaving. */
	max?: number;
	onInput: (value: number) => void;
};

/** Holds a value to the range its caller allows. */
const held = (value: number, min?: number, max?: number): number =>
	Math.min(
		Math.max(value, min ?? Number.NEGATIVE_INFINITY),
		max ?? Number.POSITIVE_INFINITY,
	);

/**
 * A number entered as text.
 *
 * A native number input cannot carry an unfinished decimal: while the text
 * reads "0." the browser reports the value as empty, so anything that echoes
 * the parsed value back into the field wipes the separator the moment it is
 * typed, and only whole numbers can be entered by keyboard. Here the field owns
 * its text and reports every value it can parse, so nothing is echoed back
 * while typing. Leaving the field tidies up: the entry is re-formatted, held to
 * the allowed range, and an entry that is no number at all falls back to the
 * value the field came in with rather than silently meaning zero.
 */
export const NumberField = (props: NumberFieldProps) => {
	const i18n = useI18n();
	const [text, setText] = createSignal(
		formatDecimal(props.value, i18n.locale()),
	);
	const [editing, setEditing] = createSignal(false);

	// A value changed elsewhere reaches an idle field. While the field is being
	// typed into, the text on screen belongs to the person typing it.
	createEffect(() => {
		const formatted = formatDecimal(props.value, i18n.locale());
		if (!untrack(editing)) setText(formatted);
	});

	const commit = () => {
		const parsed = parseDecimal(text());
		const value =
			parsed === undefined ? props.value : held(parsed, props.min, props.max);
		setEditing(false);
		setText(formatDecimal(value, i18n.locale()));
		if (value !== props.value) props.onInput(value);
	};

	return (
		<Field label={props.label} hint={props.hint} optional={props.optional}>
			{(controlId) => (
				<input
					id={controlId}
					class={styles.input}
					type="text"
					inputMode="decimal"
					value={text()}
					onFocus={() => setEditing(true)}
					onInput={(event) => {
						setEditing(true);
						setText(event.currentTarget.value);
						const parsed = parseDecimal(event.currentTarget.value);
						if (parsed !== undefined) props.onInput(parsed);
					}}
					onBlur={commit}
					onKeyDown={(event) => {
						if (event.key === "Enter") commit();
					}}
				/>
			)}
		</Field>
	);
};

export type ToggleProps = {
	label: string;
	hint?: string;
	checked: boolean;
	onToggle: (checked: boolean) => void;
};

export const Toggle = (props: ToggleProps) => (
	<label class={styles.choice}>
		<input
			type="checkbox"
			checked={props.checked}
			onChange={(event) => props.onToggle(event.currentTarget.checked)}
		/>
		{props.label}
	</label>
);

export type ButtonProps = {
	label: string;
	/** A glyph before the label. Decoration only: the label still carries the meaning. */
	icon?: JSX.Element;
	/** `primary` for the one action a screen is about, `quiet` for the rest. */
	tone?: "primary" | "quiet" | "danger";
	/** `large` for the one action a whole page exists for, such as submitting. */
	size?: "regular" | "large";
	disabled?: boolean;
	onClick: () => void;
};

export const Button = (props: ButtonProps) => (
	<button
		class={`${styles.button} ${styles[props.tone ?? "quiet"]} ${
			props.size === "large" ? styles.large : ""
		}`}
		type="button"
		disabled={props.disabled === true}
		onClick={props.onClick}
	>
		<Show when={props.icon !== undefined}>
			<span class={styles.buttonIcon} aria-hidden="true">
				{props.icon}
			</span>
		</Show>
		{props.label}
	</button>
);

export type IconButtonProps = {
	/** Names the action for the tooltip and for assistive technology. */
	label: string;
	icon: JSX.Element;
	tone?: "primary" | "quiet" | "danger";
	/** Set on a button that reveals something, so its state travels too. */
	expanded?: boolean;
	disabled?: boolean;
	onClick: () => void;
};

/**
 * A button that shows only its glyph. The label still travels, as the tooltip
 * and as the accessible name, so the action is never a guess.
 */
export const IconButton = (props: IconButtonProps) => (
	<button
		class={`${styles.button} ${styles[props.tone ?? "quiet"]} ${styles.iconOnly}`}
		type="button"
		title={props.label}
		aria-label={props.label}
		aria-expanded={props.expanded}
		disabled={props.disabled === true}
		onClick={props.onClick}
	>
		{props.icon}
	</button>
);

export type DownloadLinkProps = {
	label: string;
	href: string;
	/** The name the saved file gets, so a download is not called by its route. */
	filename: string;
};

/**
 * A download wearing the button styling. It stays an anchor because the browser
 * has to fetch the bytes itself, carrying the session cookie along.
 */
export const DownloadLink = (props: DownloadLinkProps) => (
	<a
		class={`${styles.button} ${styles.quiet}`}
		href={props.href}
		download={props.filename}
	>
		{props.label}
	</a>
);

export type FieldSetProps = {
	/** Disables every control inside, the way the platform already can. */
	disabled?: boolean;
	legend?: string;
	children: JSX.Element;
};

/**
 * A group of controls that can be switched off as one. A native fieldset does
 * this correctly for the keyboard and for assistive technology, which a class
 * name on a wrapper cannot.
 */
export const FieldSet = (props: FieldSetProps) => (
	<fieldset class={styles.fieldset} disabled={props.disabled === true}>
		<Show when={props.legend !== undefined}>
			<legend class={styles.legend}>{props.legend}</legend>
		</Show>
		{props.children}
	</fieldset>
);

export type FormProps = {
	/** The fields, laid out in one column with consistent spacing. */
	children: JSX.Element;
};

export const Form = (props: FormProps) => (
	<div class={styles.form}>{props.children}</div>
);

export const Actions = (props: FormProps) => (
	<div class={styles.actions}>{props.children}</div>
);
